import { useState, useEffect } from "react";
import { AdminField, AdminSaveButton, AdminSection } from "@/admin/AdminComponents";
import { Plus, Trash2, GripVertical } from "lucide-react";

const ICON_OPTIONS = ["Server", "Shield", "Radio", "Smartphone", "Layers", "Workflow", "Cloud", "Monitor", "Cpu", "Box"];

const CapabilitiesEditor = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({});

  useEffect(() => { setForm({ ...data }); }, [data]);

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const updateItem = (idx, key, val) => {
    const items = [...(form.items || [])];
    items[idx] = { ...items[idx], [key]: val };
    setForm((p) => ({ ...p, items }));
  };
  const removeItem = (idx) => {
    const items = [...(form.items || [])];
    items.splice(idx, 1);
    setForm((p) => ({ ...p, items }));
  };
  const addItem = () => {
    const items = [...(form.items || [])];
    items.push({ id: `cap-${Date.now()}`, title: "", description: "", icon: "Server" });
    setForm((p) => ({ ...p, items }));
  };
  const moveItem = (idx, dir) => {
    const items = [...(form.items || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    setForm((p) => ({ ...p, items }));
  };

  return (
    <div data-testid="capabilities-editor">
      <AdminSection title="Section Header">
        <AdminField label="Section Label" value={form.label} onChange={(v) => update("label", v)} />
        <AdminField label="Heading" value={form.heading} onChange={(v) => update("heading", v)} />
      </AdminSection>

      <AdminSection title="Capability Items" description="Infrastructure and capability highlights">
        {(form.items || []).map((item, idx) => (
          <div key={item.id || idx} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button onClick={() => moveItem(idx, -1)} className="p-1 text-neutral-600 hover:text-white transition-colors">
                  <GripVertical className="w-3.5 h-3.5" />
                </button>
                <span className="text-xs text-neutral-500 font-['Manrope'] font-medium">Item {idx + 1}</span>
              </div>
              <button onClick={() => removeItem(idx)} className="p-1.5 rounded-md text-neutral-600 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <AdminField label="Title" value={item.title} onChange={(v) => updateItem(idx, "title", v)} />
            <AdminField label="Description" value={item.description} onChange={(v) => updateItem(idx, "description", v)} rows={2} />
            <div>
              <label className="block text-xs font-medium text-neutral-500 font-['Manrope'] mb-2 uppercase tracking-wider">Icon</label>
              <select
                value={item.icon || "Server"}
                onChange={(e) => updateItem(idx, "icon", e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm font-['Manrope'] focus:outline-none focus:border-white/[0.2]"
              >
                {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
          </div>
        ))}
        <button
          data-testid="add-capability-button"
          onClick={addItem}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-white/[0.1] text-sm text-neutral-500 font-['Manrope'] hover:border-white/[0.2] hover:text-white transition-colors duration-200"
        >
          <Plus className="w-4 h-4" /> Add Capability
        </button>
      </AdminSection>

      <AdminSaveButton onClick={() => onSave(form)} saving={saving} />
    </div>
  );
};

export default CapabilitiesEditor;
